/**
 * Category reducers — SPA architectural convergence plan §6.1.
 *
 * The reducer is the ONLY piece of code that mutates the v1 cache
 * for a category. It enforces the live consistency protocol:
 *
 *   - Snapshots replace the entire category (`Map.clear()` then
 *     populate from snapshot envelope).
 *   - Events are sequenced; a sequence gap is detected upstream in
 *     `FetchSseTransport` which calls `recover()` (closes the
 *     connection, SPA refetches the exact snapshot).
 *   - Manifests replace the category's active-entity set; the
 *     reducer removes any locally-cached entity not present in the
 *     manifest, then applies the manifest's epoch + revision.
 *   - Tombstones remove the entity; the reducer does NOT re-merge
 *     a tombstoned entity on the next event.
 *   - Map-revision notifications update the cached
 *     `building_map_revision` so the SPA knows to refetch the
 *     exact building-map snapshot.
 *
 * Per plan §Live consistency protocol: "On reconnect, token refresh,
 * sequence gap, epoch change, or replay-window miss, the SPA
 * replaces the affected category from an exact snapshot; it must
 * not merge indefinitely."
 */
import { isValidEnvelope } from './v1-envelope';
import type {
  CategoryManifest,
  SiteId,
  Tombstone,
  V1LiveEvent,
} from './v1';

// Re-export for the per-site registry API below.
export type { SiteId };

// ----------------------------------------------------------------------------
// Generic reducer — state is keyed by entity id (string). Payloads are
// stored as opaque records; consumers cast at the boundary.
// ----------------------------------------------------------------------------

export interface ReducerState {
  byId: Map<string, unknown>;
  ids: ReadonlyArray<string>;
  manifest?: CategoryManifest;
  epoch: number | null;
  revision: number | null;
  /**
   * Ids this reducer has been told, EXPLICITLY, to retract since the
   * connection opened — a tombstone naming the id, or a manifest that
   * dropped an id the reducer was holding.
   *
   * This exists because `byId` is NOT a complete view of what the
   * screens are showing: nothing on the wire ever sends a
   * `kind: 'snapshot'`, so the reducer starts empty and only ever
   * accumulates entities that have had a live update since THIS
   * connection opened, while the cache also holds everything the REST
   * seed fetched (see `useLiveData.ts::mergeById`). "Absent from
   * `byId`" therefore means "no live update yet", never "gone" —
   * pruning the cache down to `byId` would erase most of the list on
   * the first event. A retraction is the one signal that does mean
   * gone, so it is recorded separately and is the only thing
   * `mergeById` is allowed to delete on.
   *
   * Set membership is dropped again the moment the id is re-asserted
   * (an event or a snapshot carrying it), so a tombstone followed by a
   * fresh state does not leave the entity permanently invisible.
   */
  retracted: Set<string>;
  /**
   * Per-id `projector_received_at_ms` from the envelope of the last
   * update that carried the entity — i.e. when the platform last
   * published anything about it.
   *
   * This is the only honest measure of "has this entity gone quiet".
   * The payload's own timestamps are not: a task carries
   * `started_at_ms`, which is when it BEGAN, so measuring silence
   * from it badges every healthy long-running job (`patrol.dispatch-48`
   * was orphaned at 45 h, but a normal cleaning run legitimately
   * exceeds 2 h). See `screens/Tasks/task-staleness.ts`.
   */
  receivedAt: Map<string, number>;
}

export interface ReducerHandle {
  state: ReducerState;
  apply(event: V1LiveEvent): 'applied' | 'needs_snapshot' | 'ignored';
}

export function createReducer(getId: (payload: unknown) => string): ReducerHandle {
  const state: ReducerState = {
    byId: new Map(),
    ids: [],
    epoch: null,
    revision: null,
    retracted: new Set(),
    receivedAt: new Map(),
  };

  function apply(event: V1LiveEvent): 'applied' | 'needs_snapshot' | 'ignored' {
    // Per v1.ts: "The SPA refuses to render a payload whose
    // schema_version it does not understand." Reject missing or
    // unknown schema versions — silently accepting future versions
    // would mask wire-contract drift.
    if (!isValidEnvelope(event.envelope)) {
      return 'ignored';
    }

    if (event.kind === 'snapshot') {
      // A snapshot is authoritative for the whole category, so an id
      // the reducer held and the snapshot omits is a retraction; one
      // the snapshot carries is un-retracted.
      const held = Array.from(state.byId.keys());
      state.byId.clear();
      const payload = event.payload as unknown;
      if (Array.isArray(payload)) {
        for (const item of payload) {
          state.byId.set(getId(item), item);
        }
      } else {
        state.byId.set(getId(payload), payload);
      }
      for (const id of held) {
        if (!state.byId.has(id)) {
          state.retracted.add(id);
          state.receivedAt.delete(id);
        }
      }
      for (const id of state.byId.keys()) {
        state.retracted.delete(id);
        state.receivedAt.set(id, event.envelope.projector_received_at_ms);
      }
      state.ids = Array.from(state.byId.keys());
      state.epoch = event.envelope.projector_epoch;
      state.revision = event.envelope.category_revision;
      return 'applied';
    }

    if (event.kind === 'event') {
      if (state.epoch !== null && event.envelope.projector_epoch < state.epoch) {
        return 'needs_snapshot';
      }
      const payload = event.payload as unknown;
      const id = getId(payload);
      state.byId.set(id, payload);
      // Re-asserted: the platform is publishing state for it again
      // (an id reused, or a tombstone raced by a late update). Leaving
      // it retracted would let `mergeById` delete the row it just
      // merged in, which reads to the operator as a task that will not
      // stay on screen.
      state.retracted.delete(id);
      state.receivedAt.set(id, event.envelope.projector_received_at_ms);
      state.ids = Array.from(state.byId.keys());
      return 'applied';
    }

    if (event.kind === 'manifest') {
      const manifest = event.payload as CategoryManifest;
      const keep = new Set(manifest.entities);
      for (const id of Array.from(state.byId.keys())) {
        if (!keep.has(id)) {
          state.byId.delete(id);
          state.retracted.add(id);
          state.receivedAt.delete(id);
        }
      }
      for (const id of keep) state.retracted.delete(id);
      state.ids = Array.from(state.byId.keys());
      state.manifest = manifest;
      state.epoch = manifest.epoch;
      state.revision = manifest.revision;
      return 'applied';
    }

    if (event.kind === 'tombstone') {
      const tomb = event.payload as Tombstone;
      state.byId.delete(tomb.entity_id);
      // The tombstone names the id, so this retraction is valid even
      // for an entity this reducer never saw a live update for — which
      // is the normal case for a task retired by an operator: the
      // orphaned `patrol.dispatch-48` row had not published anything
      // for 45 h, so it existed ONLY in the REST-seeded cache.
      state.retracted.add(tomb.entity_id);
      state.receivedAt.delete(tomb.entity_id);
      state.ids = Array.from(state.byId.keys());
      return 'applied';
    }

    return 'ignored';
  }

  return { state, apply };
}

// ----------------------------------------------------------------------------
// Per-category reducers
//
// Each category reducer is the SOLE mutation path for its slice of
// the TanStack Query cache. Screens wire a single reducer per
// category (one `useLiveFleetReducer`, `useLiveDoorReducer`, etc.
// hook defined in src/api/hooks/), and the SSE transport routes
// incoming events to the right reducer by category.
//
// Use these directly when you need fine-grained control of the
// reducer lifecycle (e.g. tests, custom hooks). The pre-baked
// per-site registry API (`createReducerRegistry` + `applyToRegistry`)
// was removed — the registry pattern was unused and added an
// indirection that the simpler per-category hooks don't need.
// ----------------------------------------------------------------------------

function idOf(payload: unknown, key: string): string {
  const rec = payload as { id?: Record<string, unknown> } | null;
  if (!rec || typeof rec !== 'object') return '';
  const id = rec.id;
  if (!id || typeof id !== 'object') return '';
  const v = (id as Record<string, unknown>)[key];
  return typeof v === 'string' ? v : '';
}

/**
 * Create one reducer per category for a given site. Returns the
 * pre-bound ReducerHandle set so the SSE transport can dispatch
 * incoming events to the right slice by `event.category`.
 */
export function createSiteReducerRegistry(site: SiteId) {
  return {
    site,
    fleets: createReducer((p) => idOf(p, 'fleet')),
    doors: createReducer((p) => idOf(p, 'door')),
    lifts: createReducer((p) => idOf(p, 'lift')),
    // Keyed by the BARE robot name, like every other category — NOT
    // `fleet/robot`. The wire has no fleet segment
    // (`rmf/v1/sites/{site}/robots/{robot}/state`), so a tombstone's
    // `entity_id`, a manifest entry and `mergeById`'s cache key are
    // all the bare name. A composite key here agreed with none of
    // them: the tombstone's delete missed, and — the damaging half —
    // a re-published robot could never clear itself from `retracted`,
    // so `mergeById` re-deleted it on EVERY later flush. With the
    // worker reaping any robot silent for 30 s, that meant a robot
    // that went briefly offline (or whose adapter pod was rolled)
    // never came back on an open tab.
    robots: createReducer((p) => idOf(p, 'robot')),
    tasks: createReducer((p) => idOf(p, 'task')),
    /** Latest observed `building_map_revision` for this site. */
    buildingMapRevision: undefined as string | undefined,
  };
}

export type SiteReducerRegistry = ReturnType<typeof createSiteReducerRegistry>;

export function applyToRegistry(
  registry: SiteReducerRegistry,
  event: V1LiveEvent,
): 'applied' | 'needs_snapshot' | 'ignored' {
  if (event.kind === 'snapshot' || event.kind === 'event') {
    switch (event.category) {
      case 'fleets':
        return registry.fleets.apply(event);
      case 'doors':
        return registry.doors.apply(event);
      case 'lifts':
        return registry.lifts.apply(event);
      case 'robots':
        return registry.robots.apply(event);
      case 'tasks':
        return registry.tasks.apply(event);
      default:
        return 'ignored';
    }
  }
  if (event.kind === 'manifest') {
    switch (event.payload.category) {
      case 'fleets':
        return registry.fleets.apply(event);
      case 'doors':
        return registry.doors.apply(event);
      case 'lifts':
        return registry.lifts.apply(event);
      case 'robots':
        return registry.robots.apply(event);
      case 'tasks':
        return registry.tasks.apply(event);
    }
  }
  if (event.kind === 'tombstone') {
    switch (event.payload.category) {
      case 'fleets':
        return registry.fleets.apply(event);
      case 'doors':
        return registry.doors.apply(event);
      case 'lifts':
        return registry.lifts.apply(event);
      case 'robots':
        return registry.robots.apply(event);
      case 'tasks':
        return registry.tasks.apply(event);
    }
  }
  if (event.kind === 'building_map_revision') {
    registry.buildingMapRevision = event.payload.revision;
    return 'applied';
  }
  return 'ignored';
}